import { hasTime, parseDateOnly, toBcp47, formatEventDateRange as formatEventDateRangeShared } from '@shared/dates'
import { getNow, nowMillis } from './clock'

export function formatEventDateRange(
  startDate: string,
  endDate: string | undefined | null,
  locale: string,
): string {
  return formatEventDateRangeShared(startDate, endDate, locale, getNow())
}

export function formatDayShort(dateStr: string, locale: string): string {
  return parseDateOnly(dateStr).toLocaleDateString(toBcp47(locale), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

// The full weekday name for a YYYY-MM-DD date, e.g. "Friday" / "viernes".
export function formatWeekday(dateStr: string, locale: string): string {
  return parseDateOnly(dateStr).toLocaleDateString(toBcp47(locale), { weekday: 'long' })
}

// The abbreviated weekday for a YYYY-MM-DD date, e.g. "Fri" / "vie" (es drops
// its trailing abbreviation period).
export function formatWeekdayShort(dateStr: string, locale: string): string {
  return parseDateOnly(dateStr).toLocaleDateString(toBcp47(locale), { weekday: 'short' }).replace(/\.$/, '')
}

export function eventDayRange(startDate: string, endDate?: string | null): string[] {
  const effectiveEnd = endDate ?? startDate
  const [sy, sm, sd] = startDate.slice(0, 10).split('-').map(Number)
  const [ey, em, ed] = effectiveEnd.slice(0, 10).split('-').map(Number)
  const start = Date.UTC(sy, sm - 1, sd)
  const end = Date.UTC(ey, em - 1, ed)
  const days: string[] = []
  for (let t = start; t <= end; t += 24 * 60 * 60 * 1000) {
    const d = new Date(t)
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const dd = String(d.getUTCDate()).padStart(2, '0')
    days.push(`${y}-${m}-${dd}`)
  }
  return days
}

export type EventCountdown =
  | { status: 'now' }
  | { status: 'today' }
  | { status: 'upcoming'; days: number }

export function eventCountdown(
  startDate: string,
  endDate: string | undefined | null,
  today: string,
): EventCountdown {
  const startDay = startDate.slice(0, 10)
  const endDay = (endDate ?? startDate).slice(0, 10)
  if (today >= startDay && today <= endDay) {
    return today === startDay ? { status: 'today' } : { status: 'now' }
  }
  if (today < startDay) {
    const [ty, tm, td] = today.split('-').map(Number)
    const [sy, sm, sd] = startDay.split('-').map(Number)
    const ms = Date.UTC(sy, sm - 1, sd) - Date.UTC(ty, tm - 1, td)
    return { status: 'upcoming', days: Math.round(ms / 86_400_000) }
  }
  return { status: 'now' }
}

// An event is considered active (not yet ended) based on its effective end time:
//   - endDate with a time portion (e.g. "2026-06-14T03:00") → active until that exact time
//   - endDate without time (e.g. "2026-06-14", typical for multi-day festivals) → active all day
//   - no endDate → assumes the event ends at 2 AM the day after startDate
export function isEventActive(
  event: { startDate: string; endDate?: string | null },
  now: Date,
): boolean {
  const end = event.endDate ?? event.startDate
  if (hasTime(end)) return new Date(end) > now
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  if (event.endDate) return end.slice(0, 10) >= today
  // No endDate: assume ends 2 AM the day after startDate
  const nextDay = new Date(event.startDate.slice(0, 10) + 'T02:00:00')
  nextDay.setDate(nextDay.getDate() + 1)
  return nextDay > now
}

// Whether the event's start moment has passed. For a time-bearing startDate this
// is exact; for a date-only startDate (typical multi-day festival) it's "the
// start day has arrived".
export function hasEventStarted(event: { startDate: string }, now: Date): boolean {
  const start = event.startDate
  if (hasTime(start)) return new Date(start) <= now
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return start.slice(0, 10) <= today
}

// True while the event is currently happening: it has started AND not yet ended.
// `isEventActive` covers the "not ended" half, `hasEventStarted` the other — so
// callers (e.g. the festival-mode FAB pulse) can tell "live right now" apart from
// "upcoming".
export function isEventLive(
  event: { startDate: string; endDate?: string | null },
  now: Date,
): boolean {
  return isEventActive(event, now) && hasEventStarted(event, now)
}

export type FestivalPhase = 'pre' | 'live' | 'post'

/**
 * Which side of an event we're on. Beacon is really two products — a pre-festival
 * planner and an on-site companion — and this is what lets analytics tell them
 * apart.
 */
export function festivalPhase(
  event: { startDate: string; endDate?: string | null },
  now: Date = getNow(),
): FestivalPhase {
  if (isEventLive(event, now)) return 'live'
  return hasEventStarted(event, now) ? 'post' : 'pre'
}

export type DaysToEventBucket = 'past' | 'same_day' | '1_7' | '8_30' | '31_plus'

/**
 * Planning lead time, bucketed. Bucketed rather than raw so it stays a usable
 * grouping dimension — a raw day count would be near-unique per event.
 */
export function daysToEventBucket(
  event: { startDate: string },
  now: Date = getNow(),
): DaysToEventBucket {
  const start = parseDateOnly(event.startDate.slice(0, 10))
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const days = Math.round((start.getTime() - today.getTime()) / 86_400_000)
  if (days < 0) return 'past'
  if (days === 0) return 'same_day'
  if (days <= 7) return '1_7'
  if (days <= 30) return '8_30'
  return '31_plus'
}

export function todayLocalISO(): string {
  const d = getNow()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

/**
 * The festival "day" a moment belongs to: hours before the event's day cutoff
 * (`eventDayCutoff()`) belong to the previous night's party, not the calendar
 * day the clock has just rolled into. The schedule grid anchors late-night sets
 * the same way (see ScheduleView/timeUtils).
 */
export function festivalDay(cutoff: number, now: Date = getNow()): string {
  const anchor = new Date(now)
  if (anchor.getHours() < cutoff) anchor.setDate(anchor.getDate() - 1)
  return toDateISO(anchor)
}

/**
 * `festivalDay`, scoped to a festival's set days: when the rolled-back night has
 * no sets — the small hours before the first day — it's not a night anyone is
 * still out on, so the calendar date wins if that one has sets.
 */
export function festivalDayWithin(days: readonly string[], cutoff: number, now: Date = getNow()): string {
  const day = festivalDay(cutoff, now)
  if (days.length === 0 || days.includes(day)) return day
  const calendar = toDateISO(now)
  return days.includes(calendar) ? calendar : day
}

export function yesterdayLocalISO(): string {
  const d = getNow()
  d.setDate(d.getDate() - 1)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export function toDateISO(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export const dateToISO = toDateISO

export function toTimeHM(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// Add offset minutes to an "HH:mm" base time, returning "HH:mm" display format.
export function resolveTime(baseHHmm: string, offsetMin: number): string {
  const [h, m] = baseHHmm.split(':').map(Number)
  const total = h * 60 + m + offsetMin
  const rh = Math.floor(total / 60) % 24
  const rm = total % 60
  return `${String(rh).padStart(2, '0')}:${String(rm).padStart(2, '0')}`
}

// Duration in minutes between two "HH:mm" times (handles overnight wrap).
export function timeDurationMin(startHHmm: string, endHHmm: string): number {
  const [sh, sm] = startHHmm.split(':').map(Number)
  const [eh, em] = endHHmm.split(':').map(Number)
  let diff = (eh * 60 + em) - (sh * 60 + sm)
  if (diff <= 0) diff += 24 * 60
  return diff
}

// Compact relative time ("2 min ago", "3 d ago") for feed timestamps.
export function formatRelativeTime(date: Date, locale: string): string {
  const diffSec = Math.round((date.getTime() - nowMillis()) / 1000)
  // fbtee uses underscore separators (en_US); Intl requires BCP 47 hyphens (en-US).
  const rtf = new Intl.RelativeTimeFormat(toBcp47(locale), { numeric: 'auto', style: 'short' })
  const abs = Math.abs(diffSec)
  if (abs < 60) return rtf.format(diffSec, 'second')
  const diffMin = Math.round(diffSec / 60)
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute')
  const diffHr = Math.round(diffMin / 60)
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour')
  const diffDay = Math.round(diffHr / 24)
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'day')
  const diffMonth = Math.round(diffDay / 30)
  if (Math.abs(diffMonth) < 12) return rtf.format(diffMonth, 'month')
  return rtf.format(Math.round(diffMonth / 12), 'year')
}
